import type { SurfacePageTabItem } from '@/design-system';
import type { ControlLoopState } from '@/versions/v1/schema/advisory-issue';
import { parseDwellHours } from '@/versions/v1/modules/process/Scene.transform';
import { SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { ebitdaPerOperatingHour, measuredFeedStph, stopValue } from '@/versions/v1/modules/downtime/OperatingValue.transform';
import { formatCurrency, formatCurrencyExact, CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import {
  BUDGET_T_H,
  CONTROL_VERIFICATION_CHECKS,
  OVERRIDE_CHANGE_REQUESTS,
  RISK_ENTRIES,
  SAFETY_INTERLOCKS,
  type ControlVerificationCheck,
  type OverrideAuthority,
  type OverrideChangeRequest,
  type RiskEntry,
  type SafetyInterlock,
} from './seed';

export type RiskCopy = {
  title: string;
};

export function riskCopy(): RiskCopy {
  return { title: 'Risk' };
}

/** Three tabs (locked comp `risk.html` A, adjudication 1): the overview tab
 *  dissolves into the band and the panel row; Interlocks and Control
 *  verification join into Critical controls (from C). */
export type RiskTab = 'risk-register' | 'checks' | 'controls';

export const RISK_TABS: readonly SurfacePageTabItem<RiskTab>[] = [
  { id: 'risk-register', label: 'Risk register' },
  { id: 'checks', label: 'Checks & inspections' },
  { id: 'controls', label: 'Critical controls' },
];

export function isRiskTab(value: string | null): value is RiskTab {
  return RISK_TABS.some((tab) => tab.id === value);
}

export type RiskBand = 'low' | 'medium' | 'high' | 'critical';
export type RiskScoreAxis = 1 | 2 | 3 | 4 | 5;
export type RiskBandTone = 'default' | 'warning' | 'danger';

export type RiskScore = {
  likelihood: RiskScoreAxis;
  consequence: RiskScoreAxis;
  score: number;
  band: RiskBand;
};

export const MANUAL_LOOP_LIMIT_HOURS = 4;

function clampAxis(value: number): RiskScoreAxis {
  const rounded = Math.ceil(value);
  if (rounded <= 1) return 1;
  if (rounded >= 5) return 5;
  return rounded as RiskScoreAxis;
}

export function riskBandForScore(score: number): RiskBand {
  if (score >= 12) return 'critical';
  if (score >= 9) return 'high';
  if (score >= 5) return 'medium';
  return 'low';
}

export function riskBandLabel(band: RiskBand): string {
  if (band === 'critical') return 'Critical';
  if (band === 'high') return 'High';
  if (band === 'medium') return 'Medium';
  return 'Low';
}

export function riskBandTone(band: RiskBand): RiskBandTone {
  if (band === 'critical') return 'danger';
  if (band === 'high') return 'warning';
  return 'default';
}

export function riskScore(risk: RiskEntry): RiskScore {
  const likelihood = clampAxis(risk.occurrence / 2);
  const consequence = clampAxis(Math.max(risk.safetySeverity, risk.productionSeverity) / 2);
  const score = likelihood * consequence;
  return { likelihood, consequence, score, band: riskBandForScore(score) };
}

/** The heat map's consequence lens (comp adjudication 2): the worst of both
 *  axes (the default, today's positions) or one axis alone. */
export type RiskConsequenceMode = 'worst' | 'safety' | 'production';

export const RISK_CONSEQUENCE_MODES: readonly { id: RiskConsequenceMode; label: string }[] = [
  { id: 'worst', label: 'Worst of both' },
  { id: 'safety', label: 'Safety' },
  { id: 'production', label: 'Production' },
];

/** `ceil(severity / 2)` on the chosen axis, clamped 1–5; `worst` is
 *  `riskScore().consequence` exactly. */
export function consequenceFor(risk: RiskEntry, mode: RiskConsequenceMode): RiskScoreAxis {
  if (mode === 'safety') return clampAxis(risk.safetySeverity / 2);
  if (mode === 'production') return clampAxis(risk.productionSeverity / 2);
  return riskScore(risk).consequence;
}

export function manualLoopBreaches(
  loops: readonly ControlLoopState[],
): readonly ControlLoopState[] {
  return loops.filter((loop) => {
    if (loop.mode !== 'manual') return false;
    const hours = parseDwellHours(loop.duration);
    return hours !== null && hours > MANUAL_LOOP_LIMIT_HOURS;
  });
}

// ---------------------------------------------------------------------------
// Subprocess / constraint position (moved from seed.ts — a derivation, not data)
// ---------------------------------------------------------------------------

function subprocessIsBottleneck(subprocessId: string): boolean {
  return SUBPROCESSES.find((sp) => sp.id === subprocessId)?.isBottleneck ?? false;
}

export function riskAtConstraint(risk: Pick<RiskEntry, 'subprocessId'>): boolean {
  return subprocessIsBottleneck(risk.subprocessId);
}

// ---------------------------------------------------------------------------
// Risk register — budget impact + two-axis matrix
// ---------------------------------------------------------------------------

/** Month-4 ramp Budget day, derived from BUDGET_T_H — never a bare 1,440 literal. */
const BUDGET_DAY_TONNES = BUDGET_T_H * 24;

export type BudgetImpact = {
  quantity: number;
  quantityUnit: 'st' | 'h';
  lostHours: number;
  dollars: number;
  currency: typeof CURRENCY_GLYPH;
  atConstraint: boolean;
  /** Share of the Budget day (0–1) this event's tonnage represents. */
  budgetDayShare: number | null;
};

/**
 * Effective hours respect the downstream buffer except at the constraint or
 * for a seeded plant-stop repair. All hours use the shared EBITDA/hour rate.
 * The tonnage paired with the US$ is the measured operating feed
 * (`measuredFeedStph()` — `rd-kpi-ore-td` ÷ 24 = 71.7 stph), never the typed
 * 60 t/h Budget rate; the Budget day stays the share's denominator, as
 * context (K10c item 6).
 */
export function budgetImpact(risk: RiskEntry): BudgetImpact {
  const atConstraint = riskAtConstraint(risk);
  const lostHours = atConstraint || risk.hoursSource === 'failure-repair'
    ? risk.tripHours : Math.max(0, risk.tripHours - risk.bufferHours);
  if (risk.hoursSource === 'failure-repair') {
    return {
      quantity: risk.tripHours,
      quantityUnit: 'h',
      lostHours,
      dollars: stopValue(lostHours),
      currency: CURRENCY_GLYPH,
      atConstraint,
      budgetDayShare: null,
    };
  }
  const tonnes = lostHours * measuredFeedStph();
  const dollars = stopValue(lostHours);
  const budgetDayShare = tonnes / BUDGET_DAY_TONNES;
  return { quantity: tonnes, quantityUnit: 'st', lostHours, dollars, currency: CURRENCY_GLYPH, atConstraint, budgetDayShare };
}

function occurrenceLikelihood(occurrence: number): string {
  if (occurrence <= 3) return 'Rare';
  if (occurrence <= 6) return 'Occasional';
  return 'Frequent';
}

export type RiskRegisterRow = RiskEntry & {
  impact: BudgetImpact;
  /** 1-based rank by US$ (RPN breaks ties) — rank 1 carries the crimson bar. */
  rank: number;
  /** This row's US$ against the largest row's, 0–1, for the Budget impact bar. */
  barRatio: number;
  atConstraint: boolean;
  likelihood: string;
  score: number;
  band: RiskBand;
  bandLabel: string;
  bandTone: RiskBandTone;
};

/** Sorted by budget impact (dollars) descending — RPN alone is a tie-breaker. */
export function riskRegister(): readonly RiskRegisterRow[] {
  return RISK_ENTRIES.map((risk) => {
    const scored = riskScore(risk);
    return {
      ...risk,
      rank: 0,
      barRatio: 0,
      atConstraint: false,
      impact: budgetImpact(risk),
      likelihood: occurrenceLikelihood(risk.occurrence),
      score: scored.score,
      band: scored.band,
      bandLabel: riskBandLabel(scored.band),
      bandTone: riskBandTone(scored.band),
    };
  }).sort((left, right) => {
    if (right.impact.dollars !== left.impact.dollars) {
      return right.impact.dollars - left.impact.dollars;
    }
    return right.rpn - left.rpn;
  }).map((row, index, rows) => {
    const top = rows[0]?.impact.dollars ?? 0;
    return {
      ...row,
      rank: index + 1,
      barRatio: top > 0 ? row.impact.dollars / top : 0,
      atConstraint: row.impact.atConstraint,
    };
  });
}

export type RiskMatrixLabelSide = 'bottom' | 'right' | 'left' | 'top';

export type RiskMatrixPoint = {
  id: string;
  label: string;
  xPct: number;
  yPct: number;
  atConstraint: boolean;
  labelSide: RiskMatrixLabelSide;
};

// The scatter's plot box (560 × 210 by default, plot 514 × 162 after the axis
// gutters), used only to resolve label collisions. The chart draws at the
// card's measured width, one viewBox unit per pixel so its type never shrinks
// below the 10 px floor, and passes that plot width here. Labels try the
// right first, as the comp sets them.
export const MATRIX_VIEW = { width: 560, height: 210, padLeft: 36, padTop: 14, padRight: 10, padBottom: 34 } as const;
const MATRIX_BOX_W = MATRIX_VIEW.width - MATRIX_VIEW.padLeft - MATRIX_VIEW.padRight;
const MATRIX_BOX_H = MATRIX_VIEW.height - MATRIX_VIEW.padTop - MATRIX_VIEW.padBottom;
const MATRIX_DOT_R = 5;
const MATRIX_LABEL_W = 38;
const MATRIX_LABEL_H = 11;
const MATRIX_LABEL_GAP = 5;
const MATRIX_LABEL_SIDE_ORDER: readonly RiskMatrixLabelSide[] = ['right', 'left', 'top', 'bottom'];

type MatrixPx = { x: number; y: number };
type MatrixBox = { x0: number; x1: number; y0: number; y1: number };

function matrixToPx(point: { xPct: number; yPct: number }, boxW: number): MatrixPx {
  return { x: (point.xPct / 100) * boxW, y: (point.yPct / 100) * MATRIX_BOX_H };
}

function matrixDotBox(center: MatrixPx): MatrixBox {
  return {
    x0: center.x - MATRIX_DOT_R,
    x1: center.x + MATRIX_DOT_R,
    y0: center.y - MATRIX_DOT_R,
    y1: center.y + MATRIX_DOT_R,
  };
}

function matrixLabelBox(center: MatrixPx, side: RiskMatrixLabelSide): MatrixBox {
  const half = MATRIX_LABEL_W / 2;
  const halfH = MATRIX_LABEL_H / 2;
  const edge = MATRIX_DOT_R + MATRIX_LABEL_GAP;
  switch (side) {
    case 'bottom':
      return { x0: center.x - half, x1: center.x + half, y0: center.y + edge, y1: center.y + edge + MATRIX_LABEL_H };
    case 'top':
      return { x0: center.x - half, x1: center.x + half, y0: center.y - edge - MATRIX_LABEL_H, y1: center.y - edge };
    case 'right':
      return { x0: center.x + edge, x1: center.x + edge + MATRIX_LABEL_W, y0: center.y - halfH, y1: center.y + halfH };
    case 'left':
      return { x0: center.x - edge - MATRIX_LABEL_W, x1: center.x - edge, y0: center.y - halfH, y1: center.y + halfH };
  }
}

function matrixBoxesOverlap(a: MatrixBox, b: MatrixBox): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

/**
 * Greedy label placement: process points top-to-bottom, left-to-right and
 * give each the first side (right → left → top → bottom) whose label box
 * clears every other point's dot and every already-placed label. With nine
 * points this converges without a layout pass — verified against the
 * rendered scatter, not just this heuristic.
 */
function assignMatrixLabelSides(
  points: readonly { id: string; xPct: number; yPct: number }[],
  boxW: number,
): Map<string, RiskMatrixLabelSide> {
  const centers = new Map(points.map((point) => [point.id, matrixToPx(point, boxW)] as const));
  const placedLabelBoxes: MatrixBox[] = [];
  const sides = new Map<string, RiskMatrixLabelSide>();

  const order = [...points].sort((a, b) => a.yPct - b.yPct || a.xPct - b.xPct);

  for (const point of order) {
    const center = centers.get(point.id)!;
    let chosen: RiskMatrixLabelSide = 'bottom';
    for (const side of MATRIX_LABEL_SIDE_ORDER) {
      const box = matrixLabelBox(center, side);
      const hitsDot = points.some(
        (other) => other.id !== point.id && matrixBoxesOverlap(box, matrixDotBox(centers.get(other.id)!)),
      );
      const hitsLabel = placedLabelBoxes.some((placed) => matrixBoxesOverlap(box, placed));
      chosen = side;
      if (!hitsDot && !hitsLabel) break;
    }
    sides.set(point.id, chosen);
    placedLabelBoxes.push(matrixLabelBox(center, chosen));
  }

  return sides;
}

/** Production severity → x (0–10 right), safety severity → y (0–10 up, plotted top-down). */
export function riskMatrix(plotWidth: number = MATRIX_BOX_W): readonly RiskMatrixPoint[] {
  const base = RISK_ENTRIES.map((risk) => ({
    id: risk.id,
    label: risk.label,
    xPct: (risk.productionSeverity / 10) * 100,
    yPct: 100 - (risk.safetySeverity / 10) * 100,
    atConstraint: riskAtConstraint(risk),
  }));
  const sides = assignMatrixLabelSides(base, plotWidth);
  return base.map((point) => ({ ...point, labelSide: sides.get(point.id) ?? 'bottom' }));
}

export function formatTonnes(tonnes: number): string {
  return `${Math.round(tonnes).toLocaleString('en-AU')} st`;
}

export function formatBudgetCurrency(impact: BudgetImpact): string {
  return formatCurrency(impact.dollars);
}

/** Compatibility export for the separately owned advisory analysis surface. */
export function formatUsd(dollars: number): string {
  if (dollars === 0) return '$0';
  return `$${Math.round(dollars).toLocaleString('en-AU')}`;
}

export function formatImpactQuantity(impact: BudgetImpact): string {
  return impact.quantityUnit === 'h'
    ? `${impact.quantity.toFixed(1)} h`
    : formatTonnes(impact.quantity);
}

export function formatPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/** One-line impact arithmetic for the register's detail panel, including the
 * Budget-day share the brief's worked example names (demo moment 1). */
export function impactArithmetic(risk: RiskEntry): string {
  const impact = budgetImpact(risk);
  const rate = `${formatCurrencyExact(ebitdaPerOperatingHour())}/h EBITDA`;
  if (risk.hoursSource === 'failure-repair') {
    const planned = risk.plannedStopHours === undefined ? ''
      : ` · ${risk.plannedStopHours} h planned inspection = ${formatCurrency(stopValue(risk.plannedStopHours))}`;
    return `${impact.lostHours.toFixed(1)} h avoided failure × ${rate} = ${formatBudgetCurrency(impact)}${planned}`;
  }
  const basis = impact.atConstraint
    ? `${risk.tripHours} h at the constraint`
    : `${risk.tripHours} h trip, ${risk.bufferHours} h downstream buffer`;
  return `${basis} · ${impact.lostHours.toFixed(1)} h lost × ${rate} = ${formatBudgetCurrency(impact)} · ${formatTonnes(impact.quantity)} at ${measuredFeedStph().toFixed(1)} stph measured · ${formatPct(impact.budgetDayShare ?? 0)} of the ${formatTonnes(BUDGET_DAY_TONNES)} Budget day`;
}

/** A run of comparison text; `strong` marks the lead term (600 ink-secondary). */
/** `nowrap` keeps a trailing clause (a loop's place) whole when the line wraps;
 *  `bad` sets a lead term in breach ink — the one abnormal reading in a line. */
export type RiskTextSegment = { text: string; strong?: boolean; nowrap?: boolean; bad?: boolean };

/** The selected risk's arithmetic as the comp's equation block draws it: the
 *  hours that make the loss, the one operating-hour rate, the Budget impact,
 *  and what it compares against. Same basis as `impactArithmetic`. */
export type ImpactEquation = {
  rows: readonly { label: string; value: string }[];
  total: string;
  comparison: readonly RiskTextSegment[];
};

export function impactEquation(risk: RiskEntry): ImpactEquation {
  const impact = budgetImpact(risk);
  const rate = { label: '× EBITDA', value: `${formatCurrencyExact(ebitdaPerOperatingHour())}/h` };
  const total = formatBudgetCurrency(impact);
  if (risk.hoursSource === 'failure-repair') {
    return {
      rows: [{ label: 'Avoided failure', value: `${impact.lostHours.toFixed(1)} h` }, rate],
      total,
      comparison: risk.plannedStopHours === undefined ? [] : [
        { text: 'vs ' },
        { text: formatCurrency(stopValue(risk.plannedStopHours)), strong: true },
        { text: ` for the ${risk.plannedStopHours} h planned inspection` },
      ],
    };
  }
  const trip = impact.atConstraint
    ? { label: 'Trip at the constraint', value: `${risk.tripHours} h` }
    : { label: 'Trip · buffer', value: `${risk.tripHours} h · ${risk.bufferHours} h` };
  return {
    rows: [trip, { label: 'Lost', value: `${impact.lostHours.toFixed(1)} h` }, rate],
    total,
    comparison: [
      { text: formatTonnes(impact.quantity), strong: true },
      { text: ` at ${measuredFeedStph().toFixed(1)} stph measured · ${formatPct(impact.budgetDayShare ?? 0)} of the ${formatTonnes(BUDGET_DAY_TONNES)} Budget day` },
    ],
  };
}

/** The register's largest exposure, read for the band: its US$, and what it
 *  is measured against — the planned inspection for a failure-repair row,
 *  the Budget day's short tons otherwise. */
export function largestExposure(): { id: string; dollars: string; comparison: readonly RiskTextSegment[] } | null {
  const top = riskRegister()[0];
  if (!top) return null;
  const where = top.impact.atConstraint ? 'stop at the constraint' : 'lost';
  const lead = `${top.id} · ${top.impact.lostHours.toFixed(1)} h ${where}`;
  if (top.hoursSource === 'failure-repair' && top.plannedStopHours !== undefined) {
    return {
      id: top.id,
      dollars: formatBudgetCurrency(top.impact),
      comparison: [
        { text: `${lead} vs ` },
        { text: formatCurrency(stopValue(top.plannedStopHours)), strong: true },
        { text: ` for the ${top.plannedStopHours} h inspection` },
      ],
    };
  }
  return {
    id: top.id,
    dollars: formatBudgetCurrency(top.impact),
    comparison: [
      { text: `${lead} · ` },
      { text: formatTonnes(top.impact.quantity), strong: true },
      { text: ` of the ${formatTonnes(BUDGET_DAY_TONNES)} Budget day` },
    ],
  };
}

/** The selected risk's fact list beneath its equation (comp `.facts`). */
export type RiskDetailFacts = {
  likelihood: string;
  owner: string;
  review: string;
  workOrder?: string;
  failureMode?: string;
  controlMeasure: string;
};

export function riskDetailFacts(row: RiskRegisterRow): RiskDetailFacts {
  const failureMode = row.failureModeId
    ? FAILURE_MODES.find((mode) => mode.id === row.failureModeId)?.failureMode
    : undefined;
  return {
    likelihood: row.likelihood,
    owner: row.ownerTeam,
    review: `${formatShiftDate(row.reviewDate)} · ${row.reviewCadence}`,
    workOrder: row.linkedWorkOrder,
    failureMode,
    controlMeasure: row.controlMeasure,
  };
}

// ---------------------------------------------------------------------------
// Dates — one grammar across the surface, matching the TopBar's cursor
// ("08 Jun · Day shift"). Raw seed strings are "YYYY-MM-DD" or
// "YYYY-MM-DD day shift" / "YYYY-MM-DD night shift".
// ---------------------------------------------------------------------------

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatShiftDate(raw: string): string {
  const [datePart, ...rest] = raw.split(' ');
  const [, month, day] = datePart!.split('-').map(Number);
  const dd = String(day).padStart(2, '0');
  const mon = MONTH_ABBR[month - 1];
  const shiftText = rest.join(' ');
  if (!shiftText) return `${dd} ${mon}`;
  const shiftLabel = shiftText.startsWith('night') ? 'Night shift' : 'Day shift';
  return `${dd} ${mon} · ${shiftLabel}`;
}

// ---------------------------------------------------------------------------
// Interlocks
// ---------------------------------------------------------------------------

const OVERRIDE_TIER_ORDER: Record<OverrideAuthority, number> = {
  'No one': 0,
  'Supervisor only': 1,
  'Maintenance mode': 2,
  Operator: 3,
};

export type InterlockRow = SafetyInterlock & {
  pendingChange?: OverrideChangeRequest;
};

/** No-one rows lead, per v19 M6 §4's override hierarchy; source order within a tier. */
export function interlockRegister(): readonly InterlockRow[] {
  const pendingByInterlock = new Map(
    OVERRIDE_CHANGE_REQUESTS.map((request) => [request.interlockId, request] as const),
  );
  return [...SAFETY_INTERLOCKS]
    .sort((left, right) => OVERRIDE_TIER_ORDER[left.overrideAuthority] - OVERRIDE_TIER_ORDER[right.overrideAuthority])
    .map((interlock) => ({
      ...interlock,
      pendingChange: pendingByInterlock.get(interlock.id),
    }));
}

export function overrideChangeRequests(): readonly OverrideChangeRequest[] {
  return OVERRIDE_CHANGE_REQUESTS;
}

// ---------------------------------------------------------------------------
// Control verification
// ---------------------------------------------------------------------------

export type VerificationSummary = {
  onTimePct: number;
  onTimeCount: number;
  openFailures: number;
  degradedResults: number;
  totalChecks: number;
  /** The checks that ran late, by CV id (`CV-07`). */
  lateIds: readonly string[];
};

export function verificationSummary(): VerificationSummary {
  const total = CONTROL_VERIFICATION_CHECKS.length;
  const onTime = CONTROL_VERIFICATION_CHECKS.filter((check) => check.onTime).length;
  const openFailures = CONTROL_VERIFICATION_CHECKS.filter((check) => check.lastResult === 'fail').length;
  const degradedResults = CONTROL_VERIFICATION_CHECKS.filter((check) => check.lastResult === 'degraded').length;
  return {
    onTimePct: total === 0 ? 0 : Math.round((onTime / total) * 100),
    onTimeCount: onTime,
    openFailures,
    degradedResults,
    totalChecks: total,
    lateIds: CONTROL_VERIFICATION_CHECKS.filter((check) => !check.onTime).map((check) => check.id),
  };
}

export type VerificationCheckRow = ControlVerificationCheck & {
  interlock: SafetyInterlock | undefined;
};

export function verificationChecks(): readonly VerificationCheckRow[] {
  const interlockById = new Map(SAFETY_INTERLOCKS.map((il) => [il.id, il] as const));
  return CONTROL_VERIFICATION_CHECKS.map((check) => ({
    ...check,
    interlock: interlockById.get(check.interlockId),
  }));
}
